import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, filter, map, of, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { buildApiAuthHeaders } from '../utils/http-headers.util';

export interface PatientInfo {
  patientName: string;
  mrn: string;
  appointmentType: string;
  neurologicalSpecialty: string;
}

export interface RecordingResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export interface TranscriptionResponse {
  id?: string;
  originalFileName?: string;
  transcriptionUrl?: string;
  status?: string;
  specialty?: string;
  analysisId?: string;
  error?: string;
  patientId?: number;
}

/** Checkpoint batches already on S3 for a paused visit (no audio bytes). */
export interface PausedCheckpointStatus {
  recordingId: string;
  batchCount: number;
  highestBatchIndex: number;
  fileExtension?: string | null;
}

/** Response from POST /api/upload/recordings/{recordingId} (checkpoint batch audio). */
export interface AudioUploadResponse {
  id?: string;
  originalFileName?: string;
  audioUrl?: string;
  status?: string;
}

export interface Prescription {
  medicationName?: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
  instructions?: string;
}

export interface SOAPNote {
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;
}

export interface MedicalAnalysis {
  id: string;
  transcriptUrl?: string;
  summary?: string;
  diagnosis?: string;
  prescription?: Prescription;
  soapNote?: SOAPNote;
  specialty?: string;
  analysisData?: string;
  status?: string;
}

export interface MedicalAnalysisResponse {
  success: boolean;
  analysis?: MedicalAnalysis;
  message?: string;
}

/** POST /api/transcriptions/pause-draft */
export interface PauseRecordingDraftRequest {
  recordingId: string;
  duration: string;
  specialty: string;
  appointmentType: string;
  patientId?: number;
  patientName?: string;
  /** Update this paused row instead of inserting a duplicate. */
  existingPausedTranscriptionId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class RecordingService {
  private baseUrl = environment.apiUrl;
  private transcriptionUrl = environment.transcriptionApiUrl;
  private apiKey = environment.apiKey;

  constructor(private http: HttpClient) {}

  createRecording(patientInfo: PatientInfo): Observable<RecordingResponse> {
    return this.http.post<RecordingResponse>(`${this.baseUrl}/recordings`, patientInfo)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error creating recording:', error);
          return of({
            success: false,
            message: 'Failed to create recording'
          });
        })
      );
  }

  getAppointmentTypes(): Observable<string[]> {
    return of([
      'Initial Consultation',
      'Follow-up Visit',
      'Emergency Visit',
      'Procedure',
      'Review'
    ]);
  }

  getNeurologicalSpecialties(): Observable<string[]> {
    return of([
      'General Neurology',
      'Epilepsy',
      'Headache',
      'Movement Disorders',
      'Stroke',
      'Neuromuscular',
      'Neuroimmunology',
      'Neuro-oncology'
    ]);
  }

  uploadRecording(formData: FormData): Observable<RecordingResponse> {
    return this.http.post<RecordingResponse>(`${this.baseUrl}/recordings/upload`, formData)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error uploading recording:', error);
          return of({
            success: false,
            message: 'Failed to upload recording'
          });
        })
      );
  }

  /**
   * Stitched audio for batch index `batchIndex` (1-based): filename `chunks-{n}.{ext}`, where `ext` matches the
   * recorder container (`webm` on Chrome/Android, `mp4` on iOS). Optional `totalBatches` (post-stop flush)
   * validates `n <= totalBatches` on the server.
   */
  uploadAudioCheckpointBatch(
    blob: Blob,
    recordingId: string,
    batchIndex: number,
    totalBatches?: number,
    fileExtension = 'webm'
  ): Observable<AudioUploadResponse> {
    const formData = new FormData();
    const ext = (fileExtension || 'webm').replace(/^\.+/, '') || 'webm';
    const name = `chunks-${batchIndex}.${ext}`;
    formData.append('file', blob, name);
    const path = `${this.baseUrl}/upload/recordings/${encodeURIComponent(recordingId)}`;
    let params = new HttpParams();
    if (totalBatches != null && totalBatches > 0) {
      params = params.set('totalBatches', String(totalBatches));
    }
    return this.http.post<AudioUploadResponse>(path, formData, {
      params,
      headers: buildApiAuthHeaders(),
    });
  }

  /**
   * Delete S3 draft checkpoint batches for this session ({@code {uuid}/chunks-*.webm}) when abandoning an append.
   * Final {@code recordings/final-*.mp3} is untouched. Server verifies {@code appendTranscriptionId} owns the uuid.
   */
  abandonAppendCheckpointDraft(recordingSessionId: string, appendTranscriptionId: number): Observable<void> {
    const path = `${this.baseUrl}/upload/recordings/${encodeURIComponent(recordingSessionId)}/append-draft`;
    const params = new HttpParams().set('appendTranscriptionId', String(appendTranscriptionId));
    return this.http.delete<void>(path, { params, headers: buildApiAuthHeaders() });
  }

  /**
   * Delete S3 checkpoint batches {@code chunks-{n}} for {@code n >= fromBatch} after Cancel on a resumed visit.
   * Earlier batches and the paused transcription row stay.
   */
  discardResumedCheckpointBatches(
    recordingSessionId: string,
    transcriptionId: number,
    fromBatch: number
  ): Observable<void> {
    const path = `${this.baseUrl}/upload/recordings/${encodeURIComponent(recordingSessionId)}/resumed-batches`;
    const params = new HttpParams()
      .set('transcriptionId', String(transcriptionId))
      .set('fromBatch', String(fromBatch));
    return this.http.delete<void>(path, { params, headers: buildApiAuthHeaders() });
  }

  transcribeAudio(
    audioBlob: Blob,
    patientInfo?: PatientInfo,
    duration?: string,
    options?: {
      onUploadStarted?: () => void;
      appendTranscriptionId?: number;
      resumeTranscriptionId?: number;
    }
  ): Observable<TranscriptionResponse> {
    if (audioBlob.size === 0) {
      return throwError(() => new Error('No recording data to upload'));
    }
    // Create FormData for multipart request
    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.mp3');
    
    const resolvedDuration = duration?.trim() ? duration.trim() : '00:00';
    // Create request body object and append as JSON blob
    const requestBody: Record<string, unknown> = {
      specialty: patientInfo?.neurologicalSpecialty || 'GENERAL',
      patientName: patientInfo?.patientName || '',
      mrn: patientInfo?.mrn || '',
      appointmentType: patientInfo?.appointmentType || '',
      duration: resolvedDuration
    };
    if (options?.appendTranscriptionId != null && options.appendTranscriptionId > 0) {
      requestBody['appendTranscriptionId'] = options.appendTranscriptionId;
    }
    if (options?.resumeTranscriptionId != null && options.resumeTranscriptionId > 0) {
      requestBody['resumeTranscriptionId'] = options.resumeTranscriptionId;
    }
    
    formData.append('requestBody', new Blob([JSON.stringify(requestBody)], { type: 'application/json' }));
    formData.append('recordingDuration', resolvedDuration);
    
    const url = environment.transcriptionApiUrl;
    
    let uploadStartedNotified = false;
    const notifyUploadStarted = () => {
      if (!uploadStartedNotified) {
        uploadStartedNotified = true;
        options?.onUploadStarted?.();
      }
    };

    return this.http
      .post<TranscriptionResponse>(url, formData, {
        headers: buildApiAuthHeaders(),
        observe: 'events',
        reportProgress: true
      })
      .pipe(
        tap((event: HttpEvent<TranscriptionResponse>) => {
          if (event.type === HttpEventType.UploadProgress && event.loaded > 0) {
            notifyUploadStarted();
          }
          if (event.type === HttpEventType.Sent) {
            notifyUploadStarted();
          }
        }),
        filter((event): event is HttpResponse<TranscriptionResponse> => event.type === HttpEventType.Response),
        map((response) => {
          const body = response.body;
          if (!body) {
            throw new Error('Empty response from transcription service');
          }
          const st = (body.status ?? '').toString().trim().toUpperCase();
          // Do not treat FAILED (or other terminal outcomes) as HTTP-level errors — the row was still created/updated.
          // Throwing here triggered fallback upload and duplicate transcriptions.
          if (st === 'ERROR' || st === 'UNAUTHORIZED') {
            throw new Error(body.error || 'Failed to transcribe audio');
          }
          return body;
        }),
        catchError((error: HttpErrorResponse | Error) => {
          console.error('Error transcribing audio:', error);
          let errorMessage = 'Failed to upload recording. Please try again.';
          if (error instanceof HttpErrorResponse) {
            errorMessage = error.error?.error || error.error?.message || error.message || errorMessage;
          } else if (error instanceof Error) {
            errorMessage = error.message;
          }
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  /**
   * Stitch S3 checkpoint batches for this recording id and start the same async transcription flow as upload-and-transcribe.
   */
  finalizeCheckpointRecording(
    recordingId: string,
    totalBatches: number,
    patientInfo?: PatientInfo,
    duration?: string,
    resumeTranscriptionId?: number,
    appendTranscriptionId?: number
  ): Observable<TranscriptionResponse> {
    const formData = new FormData();
    const resolvedDuration = duration?.trim() ? duration.trim() : '00:00';
    const requestBody = {
      specialty: patientInfo?.neurologicalSpecialty || 'GENERAL',
      patientName: patientInfo?.patientName || '',
      mrn: patientInfo?.mrn || '',
      appointmentType: patientInfo?.appointmentType || '',
      duration: resolvedDuration,
    };
    formData.append('requestBody', new Blob([JSON.stringify(requestBody)], { type: 'application/json' }));
    // Plain field: some stacks bind multipart JSON without `duration`; server prefers this for paused→finalize duration.
    formData.append('recordingDuration', resolvedDuration);
    formData.append('recordingId', recordingId);
    formData.append('totalBatches', String(totalBatches));
    if (resumeTranscriptionId != null && resumeTranscriptionId > 0) {
      formData.append('resumeTranscriptionId', String(resumeTranscriptionId));
    }
    if (appendTranscriptionId != null && appendTranscriptionId > 0) {
      formData.append('appendTranscriptionId', String(appendTranscriptionId));
    }

    const url = `${environment.apiUrl}/finalize-checkpoint-recording`;

    return this.http.post<TranscriptionResponse>(url, formData, { headers: buildApiAuthHeaders() }).pipe(
      map((body) => {
        if (!body) {
          throw new Error('Empty response from finalize checkpoint recording');
        }
        if (body.status === 'CLIENT_FALLBACK') {
          return body;
        }
        const st = (body.status ?? '').toString().trim().toUpperCase();
        // Server may return 200 with status Failed + error after sync processing (e.g. short transcript).
        // Treat only hard API failures as errors so we do not run client fallback and create a duplicate row.
        if (st === 'ERROR' || st === 'UNAUTHORIZED') {
          throw new Error(body.error || 'Failed to finalize checkpoint recording');
        }
        return body;
      }),
      catchError((error: HttpErrorResponse | Error) => {
        console.error('Error finalizing checkpoint recording:', error);
        let errorMessage = 'Failed to finalize recording from checkpoints. Please try again.';
        if (error instanceof HttpErrorResponse) {
          const body = error.error;
          const bodyStatus =
            body && typeof body === 'object' && 'status' in body
              ? String((body as TranscriptionResponse).status)
              : '';
          const detailsMsg =
            body &&
            typeof body === 'object' &&
            'details' in body &&
            body.details &&
            typeof (body as { details?: { error?: string } }).details === 'object'
              ? (body as { details: { error?: string } }).details.error
              : null;
          errorMessage =
            (body && typeof body === 'object' ? (body as TranscriptionResponse).error : null) ||
            detailsMsg ||
            (body && typeof body === 'object' ? (body as { message?: string }).message : null) ||
            (bodyStatus ? `Finalize failed (${bodyStatus})` : null) ||
            error.message ||
            errorMessage;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Batches already stored for this paused visit. Empty {@code batchCount} means the server has no audio to restore.
   */
  getPausedCheckpointStatus(recordingId: string, transcriptionId: number): Observable<PausedCheckpointStatus> {
    const path = `${this.baseUrl}/upload/recordings/${encodeURIComponent(recordingId)}/checkpoints`;
    const params = new HttpParams().set('transcriptionId', String(transcriptionId));
    return this.http.get<PausedCheckpointStatus>(path, { params, headers: buildApiAuthHeaders() });
  }

  pauseRecordingDraft(body: PauseRecordingDraftRequest): Observable<TranscriptionResponse> {
    const url = `${this.baseUrl}/transcriptions/pause-draft`;
    return this.http.post<TranscriptionResponse>(url, body, {
      headers: buildApiAuthHeaders('application/json'),
    }).pipe(
      map((res) => {
        if (!res) {
          throw new Error('Empty response from pause-draft');
        }
        const st = (res.status ?? '').toString().toUpperCase();
        if (st === 'ERROR' || res.error) {
          throw new Error(res.error || 'Failed to save paused visit');
        }
        return res;
      }),
      catchError((error: HttpErrorResponse | Error) => {
        const msg =
          error instanceof HttpErrorResponse
            ? error.error?.error || error.error?.message || error.message
            : error.message;
        return throwError(() => new Error(msg || 'Failed to save paused visit'));
      })
    );
  }

  analyzeTranscription(transcriptionUrl: string, transcriptionResultId: string, specialty?: string): Observable<MedicalAnalysis> {
    const requestBody = {
      transcriptUrl: transcriptionUrl,
      transcriptionResultId: parseInt(transcriptionResultId),
      specialty: 'PRIMARYCARE'
    };

    // Headers (JWT token and API key) are automatically added by authInterceptor
    // Only set Content-Type here as it's needed for JSON body
    return this.http.post<MedicalAnalysis>(`${this.baseUrl}/medical-analysis/analyze`, requestBody, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }
}
